import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, timeout } from 'rxjs';
import { SpringPage } from './sftp-api';

export interface PresentmentHeader {
  id: number;
  filename?: string;
  entityCode?: string;
  brand?: string;
  subacquirerCode?: string;
  processDate?: string;
  processTime?: string;
  description?: string;
  recordCount?: number;
  trailerEntityCode?: string;
  trailerSubacquirerCode?: string;
  receivedAt?: string;
  processedAt?: string;
  processingMode?: string;
}

export interface PresentmentDetail {
  id: number;
  merchantNumber?: string;
  centralMerchantNumber?: string;
  processDate?: string;
  transactionAmount?: number;
  currencyCode?: string;
  cardNumberMasked?: string;
  productType?: string;
  movementCode?: string;
  movementDescription?: string;
  installments?: number;
  operationDate?: string;
  operationTime?: string;
  originalOperationDate?: string;
  paymentDate?: string;
  authorizationCode?: string;
  terminalNumber?: string;
  batchNumber?: string;
  voucherNumber?: string;
  trackingNumber?: string;
  merchantCategoryCode?: string;
  transactionId?: number;
  feeAmount?: number;
  feeTaxAmount?: number;
  financialCostAmount?: number;
  financialCostTaxAmount?: number;
  arn?: string;
  rrn?: string;
  riskFeeAmount?: number;
  riskFeeTaxAmount?: number;
  totalAmount?: number;
}

export type ReconciliationType = 'PRESENTMENT' | 'SETTLEMENT';

export interface DailyReconciliationSummary {
  date: string;
  type: ReconciliationType;
  processed: number;
  reconciled: number;
  pending: number;
}

export interface PendingReconciliation {
  id: number; type: ReconciliationType; date: string; amount?: number; currency?: string;
  authorizationCode?: string; rrn?: string; bin?: string; last4?: string; brand?: string;
  merchant?: string; terminal?: string;
}

export interface ReconciliationCandidate {
  id: number; transactionId?: string; dateTime?: string; amount?: number;
  authorizationCode?: string; rrn?: string; bin?: string; last4?: string;
  brand?: string; merchant?: string; terminal?: string; score: number; matchedFields: string[];
}

@Injectable({ providedIn: 'root' })
export class ReconciliationApi {
  constructor(private readonly http: HttpClient) {}

  getPresentmentHeaders(page: number, size: number, dateFrom: string, dateTo: string): Observable<SpringPage<PresentmentHeader>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    return this.http.get<SpringPage<PresentmentHeader>>('/reconciliation/v1/presentments', { params }).pipe(timeout(30000));
  }

  getPresentmentDetails(headerId: number, page: number, size: number): Observable<SpringPage<PresentmentDetail>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<SpringPage<PresentmentDetail>>(`/reconciliation/v1/presentments/${headerId}/details`, { params }).pipe(timeout(30000));
  }

  getPresentmentHeader(headerId: number): Observable<PresentmentHeader> {
    return this.http.get<PresentmentHeader>(`/reconciliation/v1/presentments/${headerId}`).pipe(timeout(30000));
  }

  getDailySummary(type: ReconciliationType, dateFrom: string, dateTo: string): Observable<DailyReconciliationSummary[]> {
    let params = new HttpParams().set('type', type);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    return this.http.get<DailyReconciliationSummary[]>('/reconciliation/v1/daily', { params }).pipe(timeout(30000));
  }

  getPending(type: ReconciliationType, dateFrom: string, dateTo: string, page: number, size: number): Observable<SpringPage<PendingReconciliation>> {
    let params = new HttpParams().set('type', type).set('page', page).set('size', size);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    return this.http.get<SpringPage<PendingReconciliation>>('/reconciliation/v1/manual/pending', { params }).pipe(timeout(30000));
  }

  getCandidates(type: ReconciliationType, sourceId: number): Observable<ReconciliationCandidate[]> {
    return this.http.get<ReconciliationCandidate[]>(`/reconciliation/v1/manual/${type}/${sourceId}/candidates`).pipe(timeout(30000));
  }

  confirmManual(type: ReconciliationType, sourceId: number, transactionId: number, comment: string): Observable<unknown> {
    return this.http.post(`/reconciliation/v1/manual/${type}/${sourceId}/confirm`, { transactionId, comment }).pipe(timeout(30000));
  }
}
